import { Lightbulb, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";

interface PlaceholderContent {
  title: string;
  subtitle: string;
  description: string;
  bullets?: string[];
}

const placeholderContent: Record<string, PlaceholderContent> = {
  'business-optimization': {
    title: 'Business Optimization Review',
    subtitle: 'Coming in your personalized plan',
    description: 'Once you\'ve completed your assessments, this module will generate personalized insights for your practice — including:',
    bullets: [
      'How your operational metrics compare to similar Canadian practices',
      'Your top 3 optimization opportunities ranked by succession impact',
      'Estimated valuation impact of addressing key gaps',
      'Prioritized action recommendations with effort-vs-impact scoring',
    ],
  },
  default: {
    title: 'Assessment Coming Soon',
    subtitle: 'This module is being prepared',
    description: 'This assessment will be available shortly.',
  },
};

interface AssessmentPlaceholderProps {
  moduleId: string;
  onExit?: () => void;
}

export function AssessmentPlaceholder({ moduleId, onExit }: AssessmentPlaceholderProps) {
  const navigate = useNavigate();
  const content = placeholderContent[moduleId] || placeholderContent.default;

  const handleReturn = () => {
    if (onExit) {
      onExit();
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="h-full flex items-center justify-center px-4">
      <div className="text-center max-w-lg">
        <div className="rounded-full bg-muted p-4 mx-auto w-fit mb-6">
          <Lightbulb className="h-8 w-8 text-muted-foreground" />
        </div>

        <h2 className="text-2xl font-semibold text-foreground mb-1">
          {content.title}
        </h2>
        <p className="text-muted-foreground mb-6">{content.subtitle}</p>

        <p className="text-sm text-muted-foreground mb-4">{content.description}</p>

        {content.bullets && (
          <ul className="text-sm text-muted-foreground text-left space-y-2 mb-6 mx-auto max-w-md">
            {content.bullets.map((item, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}

        <Badge variant="secondary" className="mb-8">
          Available after assessments are complete
        </Badge>

        <div className="mt-6">
          <Button onClick={handleReturn} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Return to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
